
import React from "react";

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  priority?: boolean; // If true, loads image eagerly
  placeholderSrc?: string; // (Optional) Placeholder image
  sources?: {
    webp?: string;
    avif?: string;
    fallback?: string;
  };
  sizes?: string; // For srcSet responsiveness
}

/**
 * OptimizedImage delivers AVIF/WebP if available, then falls back to default.
 * Preloads critical images if `priority` is true.
 * Always sets width/height to prevent CLS.
 */
export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  placeholderSrc,
  sources,
  sizes,
  ...rest
}) => {
  // Can preload if priority is true
  React.useEffect(() => {
    if (priority && typeof window !== "undefined") {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = sources?.avif || sources?.webp || src;
      document.head.appendChild(link);
      return () => {
        document.head.removeChild(link);
      };
    }
  }, [priority, src, sources]);

  return (
    <picture>
      {sources?.avif && (
        <source srcSet={sources.avif} type="image/avif" sizes={sizes} />
      )}
      {sources?.webp && (
        <source srcSet={sources.webp} type="image/webp" sizes={sizes} />
      )}
      {/* fallback to main src */}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        style={{
          width: width ? `${width}px` : undefined,
          height: height ? `${height}px` : undefined,
          objectFit: "cover",
          background: placeholderSrc
            ? `url(${placeholderSrc}) center center / cover no-repeat`
            : undefined,
        }}
        {...rest}
      />
    </picture>
  );
};

